import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Form state with validation that shows errors when a field has been visited or the
 * form submitted — never while someone is still mid-word in a field they have not
 * left yet. Once a field is showing an error it re-validates as they type, so the
 * message clears the moment it is fixed.
 *
 * Server `fieldErrors` land in the same error map, so an API rejection renders on
 * the field that caused it rather than only in a banner.
 */
export default function useForm({ initialValues, validators = {}, crossField }) {
  const [values, setValues] = useState(initialValues);
  const initialRef = useRef(initialValues);
  const [touched, setTouched] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  // Cheap enough to recompute each render, and always consistent with `values`.
  const clientErrors = {};
  for (const [name, validate] of Object.entries(validators)) {
    const message = validate(values[name], values);
    if (message) clientErrors[name] = message;
  }
  if (crossField) {
    for (const [name, message] of Object.entries(crossField(values) ?? {})) {
      if (message && !clientErrors[name]) clientErrors[name] = message;
    }
  }

  const errors = { ...clientErrors, ...serverErrors };
  const isValid = Object.keys(clientErrors).length === 0;

  const setValue = useCallback((name, value) => {
    setValues((current) => ({ ...current, [name]: value }));
    // A field the server complained about is given a clean slate once edited.
    setServerErrors((current) => {
      if (!(name in current)) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  }, []);

  const markTouched = useCallback((name) => {
    setTouched((current) => (current[name] ? current : { ...current, [name]: true }));
  }, []);

  const showError = (name) => Boolean(touched[name] && errors[name]);
  const errorFor = (name) => (showError(name) ? errors[name] : null);

  /** Props for a plain text/email/password/select control. */
  const field = useCallback(
    (name, extra = {}) => ({
      id: name,
      name,
      value: values[name] ?? '',
      onChange: (event) => setValue(name, event.target.value),
      onBlur: () => markTouched(name),
      'aria-invalid': touched[name] && errors[name] ? 'true' : undefined,
      'aria-describedby': touched[name] && errors[name] ? `${name}-error` : undefined,
      ...extra,
    }),
    // errors is derived from values, so values is the real dependency.
    [values, touched, setValue, markTouched], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** For file inputs and custom controls that do not carry a string value. */
  const setFieldValue = useCallback(
    (name, value) => {
      setValue(name, value);
      markTouched(name);
    },
    [setValue, markTouched],
  );

  /**
   * Maps an ApiError's fieldErrors onto this form. Names the server uses match the
   * request body, which matches these field names.
   */
  const applyServerErrors = useCallback((apiError, codeToField = {}) => {
    const mapped = {};
    for (const { field: name, message } of apiError?.fieldErrors ?? []) {
      if (name && message) mapped[name] = message;
    }
    // e.g. `mobile_taken` is a 409 with no fieldErrors, but it is still about the
    // mobile field, so it renders there instead of only in the banner.
    const byCode = codeToField[apiError?.code];
    if (Object.keys(mapped).length === 0 && byCode) {
      for (const name of [].concat(byCode)) mapped[name] = apiError.message;
    }
    if (Object.keys(mapped).length === 0) return false;
    setServerErrors(mapped);
    setTouched((current) => ({ ...current, ...Object.fromEntries(Object.keys(mapped).map((k) => [k, true])) }));
    return true;
  }, []);

  const handleSubmit = (onValid) => async (event) => {
    event.preventDefault();
    setTouched(Object.fromEntries(Object.keys(validators).map((name) => [name, true])));
    setServerErrors({});

    const invalid = Object.keys(clientErrors);
    if (invalid.length > 0) {
      // Send focus to the first problem so the fix is one keystroke away, even when
      // the offending field has scrolled out of view.
      const control = formRef.current?.elements?.[invalid[0]];
      if (control?.focus) control.focus();
      return;
    }

    setSubmitting(true);
    try {
      await onValid(values);
    } finally {
      setSubmitting(false);
    }
  };

  const formProps = useMemo(() => ({ ref: formRef, noValidate: true }), []);

  const reset = useCallback((next) => {
    setValues(next ?? initialRef.current);
    setTouched({});
    setServerErrors({});
  }, []);

  return {
    values,
    errors,
    touched,
    isValid,
    submitting,
    field,
    errorFor,
    showError,
    setValue,
    setFieldValue,
    markTouched,
    applyServerErrors,
    handleSubmit,
    formProps,
    reset,
  };
}
